import React, { useEffect, useState, useRef } from 'react';
import {
    Box, Button, Paper, Typography, Chip, Skeleton, LinearProgress,
    TextField, Tooltip, IconButton, Divider, FormControl, InputLabel,
    InputAdornment, MenuItem, Select,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination
} from '@mui/material';
import { green, red, blue, orange, grey } from '@mui/material/colors';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import api from '../../services/api';

// ─── Chip de contador (linha do resumo, sem Paper próprio) ───────────────
function Counter({ icon, label, value, color }) {
    return (
        <Box display="flex" alignItems="center" gap={0.75}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
            <Typography variant="caption" sx={{ color: grey[600] }}>{label}</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color }}>{value}</Typography>
        </Box>
    );
}

// ─── Bloco "Em andamento" inline ────────────────────────────────────────
function InProgressBlock({ items }) {
    if (items == null) return null;

    if (items.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: grey[600], fontStyle: 'italic' }}>
                Nenhum host em execução ou aguardando.
            </Typography>
        );
    }

    const running = items.filter(i => i.status === 'running');
    const queued = items.filter(i => i.status === 'queued');

    return (
        <Box>
            {running.length > 0 && (
                <Box mb={queued.length > 0 ? 1.5 : 0}>
                    <Typography variant="caption" sx={{ color: grey[600], textTransform: 'uppercase' }}>
                        Rodando agora ({running.length})
                    </Typography>
                    {running.map((h) => (
                        <Box key={h.id} mt={0.5} p={0.75}
                             sx={{ bgcolor: blue[50], borderRadius: 1,
                                   border: `1px solid ${blue[100]}` }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {h.host_name}
                                        <Typography component="span" variant="caption" sx={{ color: grey[600], ml: 1 }}>
                                            {h.ip_address} · {h.vendor} {h.type}
                                        </Typography>
                                    </Typography>
                                    {h.progress?.step && (
                                        <Typography variant="caption" sx={{ color: grey[600] }}>{h.progress.step}</Typography>
                                    )}
                                </Box>
                                {typeof h.progress?.percent === 'number' && (
                                    <Box width={120}>
                                        <LinearProgress variant="determinate"
                                                        value={Math.min(100, h.progress.percent)}
                                                        sx={{ height: 6, borderRadius: 3 }} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {queued.length > 0 && (
                <Box>
                    <Typography variant="caption" sx={{ color: grey[600], textTransform: 'uppercase' }}>
                        Na fila ({queued.length})
                    </Typography>
                    <Box display="flex" flexDirection="column" mt={0.5}>
                        {queued.slice(0, 8).map((h) => (
                            <Box key={h.id} display="flex" alignItems="center" gap={1}
                                 sx={{ py: 0.4, px: 0.5, borderRadius: 0.5,
                                       '&:hover': { bgcolor: grey[50] } }}>
                                <ScheduleIcon sx={{ fontSize: 14, color: orange[700] }} />
                                <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                                    {h.host_name}
                                    <Typography component="span" variant="caption" sx={{ color: grey[600], ml: 1 }}>
                                        {h.ip_address}
                                    </Typography>
                                </Typography>
                                {h.position != null && (
                                    <Chip label={`#${h.position}`} size="small" variant="outlined" sx={{ height: 18 }} />
                                )}
                            </Box>
                        ))}
                        {queued.length > 8 && (
                            <Typography variant="caption" sx={{ color: grey[600], pl: 1, pt: 0.3 }}>
                                + {queued.length - 8} hosts na fila
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function ResultCell({ result, message }) {
    if (result === 'error') {
        return (
            <Box display="flex" alignItems="center" gap={0.75}>
                <ErrorIcon sx={{ color: red[600], fontSize: 18 }} />
                <Box>
                    <Typography variant="body2" sx={{ color: red[700], fontWeight: 600 }}>Falhou</Typography>
                    {message && (
                        <Tooltip title={message}>
                            <Typography variant="caption" sx={{ color: grey[600], display: 'block', maxWidth: 360 }} noWrap>
                                {message}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        );
    }
    return (
        <Box display="flex" alignItems="center" gap={0.75}>
            <CheckCircleIcon sx={{ color: green[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: green[700], fontWeight: 600 }}>OK</Typography>
        </Box>
    );
}

// ─── Componente principal ───────────────────────────────────────────────
export default function Queue() {
    const [status, setStatus] = useState(null);
    const [active, setActive] = useState([]);
    const [history, setHistory] = useState({ data: [], total: 0 });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [busca, setBusca] = useState('');
    const [buscaAplicada, setBuscaAplicada] = useState('');
    const [resultado, setResultado] = useState('');
    const [loadingHistory, setLoadingHistory] = useState(true);
    const liveIntervalRef = useRef(null);

    const fetchLive = async () => {
        try {
            const [s, a] = await Promise.all([
                api.get('/hermes/queue/status').catch(() => null),
                api.get('/hermes/queue/active').catch(() => null),
            ]);
            if (s?.data) setStatus(s.data);
            if (a?.data?.data) setActive(a.data.data);
        } catch (e) { /* silencioso */ }
    };

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            // sem ?active → traz TUDO da empresa, ordenado pelo mais recente.
            const res = await api.post('/hermes/queue', {
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                order_by: 'created_at',
                order: 'desc',
                query: buscaAplicada || '',
                date_from: dateFrom || null,
                date_to: dateTo || null,
                resultado: resultado || null,
            });
            setHistory({ data: res.data?.data || [], total: res.data?.total || 0 });
        } catch (e) {
            setHistory({ data: [], total: 0 });
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        document.title = 'IPV7 Hermes - Execuções de Backup';
        fetchLive();
        liveIntervalRef.current = setInterval(fetchLive, 5000);
        return () => clearInterval(liveIntervalRef.current);
    }, []);

    useEffect(() => {
        fetchHistory();
    }, [page, rowsPerPage, dateFrom, dateTo, buscaAplicada, resultado]);

    const refreshAll = () => {
        fetchLive();
        fetchHistory();
    };

    return (
        <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff' }}>
            {/* HEADER + RESUMO */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1.5}>
                <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500 }}>
                    Execuções de Backup
                </Typography>
                <Tooltip title="Atualizar tudo">
                    <IconButton size="small" onClick={refreshAll}><RefreshIcon /></IconButton>
                </Tooltip>
            </Box>

            {status ? (
                <Box display="flex" gap={3} alignItems="center" flexWrap="wrap" mb={2}>
                    <Counter icon={<ScheduleIcon sx={{ fontSize: 18 }} />}    label="Na fila"    value={status.queue_total}   color={orange[700]} />
                    <Counter icon={<SyncIcon sx={{ fontSize: 18 }} />}        label="Rodando"    value={status.running_total} color={blue[700]} />
                    <Counter icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} label="OK hoje"    value={status.success_today} color={green[700]} />
                    <Counter icon={<ErrorIcon sx={{ fontSize: 18 }} />}       label="Falha hoje" value={status.failed_today}  color={red[700]} />
                </Box>
            ) : (
                <Skeleton variant="rectangular" height={30} sx={{ mb: 2 }} />
            )}

            <Divider sx={{ mb: 2 }} />

            {/* EM ANDAMENTO */}
            <Box mb={2}>
                <InProgressBlock items={active} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* HISTÓRICO */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Histórico de execuções
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <TextField size="small" label="Host ou IP"
                               value={busca}
                               onChange={(e) => setBusca(e.target.value)}
                               onKeyDown={(e) => {
                                   // Enter aplica. Cada tecla aqui seria uma ida ao
                                   // servidor pra uma busca que so vale digitada inteira.
                                   if (e.key === 'Enter') { setBuscaAplicada(busca); setPage(0); }
                                   if (e.key === 'Escape') { setBusca(''); setBuscaAplicada(''); setPage(0); }
                               }}
                               onBlur={() => { if (busca !== buscaAplicada) { setBuscaAplicada(busca); setPage(0); } }}
                               InputProps={{
                                   startAdornment: (
                                       <InputAdornment position="start">
                                           <SearchIcon fontSize="small" />
                                       </InputAdornment>
                                   )
                               }}
                               sx={{ width: 220 }} />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Resultado</InputLabel>
                        <Select label="Resultado" value={resultado}
                                onChange={(e) => { setResultado(e.target.value); setPage(0); }}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="error">Falhou</MenuItem>
                            <MenuItem value="ok">OK</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField type="date" size="small" label="De"
                               value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                               InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                    <TextField type="date" size="small" label="Até"
                               value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                               InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                    {(dateFrom || dateTo || buscaAplicada || resultado) && (
                        <Button size="small" startIcon={<CloseIcon />}
                                sx={{ textTransform: 'none' }}
                                onClick={() => {
                                    setDateFrom(''); setDateTo('');
                                    setBusca(''); setBuscaAplicada('');
                                    setResultado(''); setPage(0);
                                }}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Host</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Resultado</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Quando</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingHistory ? (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Skeleton variant="rectangular" height={120} />
                                </TableCell>
                            </TableRow>
                        ) : history.data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4, color: grey[600] }}>
                                    {(dateFrom || dateTo || buscaAplicada || resultado) ? (
                                        <>
                                            <Typography variant="body2">
                                                Nenhuma execução com os filtros atuais.
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: grey[500] }}>
                                                Tente ampliar o range ou limpar os filtros.
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body2">
                                                Sem execuções registradas pra essa empresa.
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: grey[500] }}>
                                                O histórico aparece conforme o worker processa backups.
                                            </Typography>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : (
                            history.data.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{row.host_name}</Typography>
                                        <Typography variant="caption" sx={{ color: grey[600] }} display="block">
                                            {row.ip_address}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: grey[600], fontSize: '0.68rem' }}>
                                            {row.vendor} — {row.type}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <ResultCell result={row.result} message={row.result_message} />
                                    </TableCell>
                                    <TableCell>
                                        {moment(row.created_at).locale('pt-br').format('DD/MM/YYYY HH:mm:ss')}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={history.total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100, 250]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
        </Paper>
    );
}
